import {
  SkyButton,
  SkyDatePicker,
  SkyDateTimePicker,
  SkyDropdown,
  SkyInput,
  SkyRadioGroup,
  SkySwitch,
  SkyText,
  SkyTextarea,
} from "@skypointcloud/skypoint-ui";
import { useCallback, useId, useRef, useState, type ReactElement } from "react";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { AddIcon, BrowseIcon, DeleteIcon } from "@/lib/icons";
import { logger } from "@/lib/logger";
import type { FieldDefinition, SelectOption } from "./types";

/**
 * One form control, chosen by the definition's `controlType` (FR-40).
 *
 * Ported from skypoint-studio's `FieldRenderer.tsx`. The control set was
 * already skypoint-ui, so what moved is the styling and the event signatures:
 * Fluent's `makeStyles` rules became semantic Tailwind classes (GUIDELINES.md
 * §3.2), the three `@fluentui/react-icons` glyphs now come from `lib/icons.ts`
 * (§3.1), `console.*` became `logger` (§4), and every callback was retargeted
 * at skypoint-ui as pinned in package.json (2.4.0 then, 2.18.1 now; the
 * signatures below held across both): `SkyInput` takes a real
 * `ChangeEvent`, `SkySwitch` answers `onCheckedChange(checked)`, and the
 * single checkbox goes through `SingleCheckbox` rather than a one-entry
 * `options` array.
 *
 * Accessibility (NFR-7): the library controls bind their own `<label for>`,
 * `aria-invalid` and `aria-describedby`, so a field only has to hand them
 * `label`, `hint` and `validationMessage`. The three composite controls — file,
 * folder selector and key/value — have no library equivalent, so they build the
 * same wiring by hand from a `useId` root rather than leaving a bare input with
 * a floating caption next to it.
 */

export interface FieldRendererProps {
  field: FieldDefinition;
  value: unknown;
  /** The message to show, or null. The caller decides when errors become visible. */
  error: string | null;
  disabled: boolean;
  /**
   * The reader may not change the value (#1241). A password then shows as a fixed mask with no
   * show/hide control, since the control would reveal nothing and invite a click.
   */
  readOnly?: boolean;
  required: boolean;
  onChange: (fieldId: string, value: unknown) => void;
  /**
   * Opens the remote browser for a `folderselector` field (FR-45). Absent when
   * the connector cannot browse, which disables the button rather than hiding
   * it, so the control still explains itself.
   */
  onBrowse?: (fieldId: string) => void;
  testIdPrefix?: string;
}

/** What a reader who cannot change a password sees in place of its value. */
const MASK_FOR_READER = "\u2022";

type KeyValuePair = { id: string; key: string; value: string };

let pairSequence = 0;
/** A stable React key for a key/value row. Never leaves the browser. */
function nextPairId(): string {
  pairSequence += 1;
  return `pair-${pairSequence}`;
}

function toPairs(value: unknown): KeyValuePair[] {
  if (Array.isArray(value) && value.length > 0) {
    return (value as Partial<KeyValuePair>[]).map((pair) => ({
      id: pair.id ?? nextPairId(),
      key: pair.key ?? "",
      value: pair.value ?? "",
    }));
  }
  return [{ id: nextPairId(), key: "", value: "" }];
}

/** "YYYY-MM-DD" → Date, or null when it is not a date. */
function parseDateValue(value: unknown): Date | null {
  if (!value) return null;
  const text = String(value);
  if (!text) return null;
  const parsed = new Date(`${text}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseDateTimeValue(value: unknown): Date | null {
  if (!value) return null;
  const text = String(value);
  if (!text) return null;
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateValue(date: Date | null): string {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateTimeValue(date: Date | null): string {
  if (!date) return "";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${formatDateValue(date)}T${hours}:${minutes}`;
}

function toDropdownOptions(options: SelectOption[] | undefined) {
  return (options ?? []).map((option) => ({
    text: option.text || option.value,
    value: option.value,
  }));
}

function toRadioOptions(options: SelectOption[] | undefined) {
  return (options ?? []).map((option) => ({
    label: option.text || option.value,
    value: option.value,
  }));
}

/** The line under a composite field: its error (announced), else its help text, else nothing. */
function compositeNote(
  controlId: string,
  error: string | null,
  helpText: string | undefined,
): ReactElement | null {
  if (error) {
    return (
      <SkyText as="span" size={200} color="danger" id={`${controlId}-error`} role="alert">
        {error}
      </SkyText>
    );
  }
  if (helpText) {
    return (
      <SkyText as="span" size={200} color="muted" id={`${controlId}-hint`}>
        {helpText}
      </SkyText>
    );
  }
  return null;
}

/**
 * The label, help and error wiring the composite controls need, matching what
 * the library controls render for themselves.
 */
function CompositeField({
  labelId,
  controlId,
  label,
  required,
  helpText,
  error,
  children,
}: {
  labelId: string;
  controlId: string;
  label: string;
  required: boolean;
  helpText?: string;
  error: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label id={labelId} htmlFor={controlId} className="text-sm font-medium text-foreground">
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </label>
      {children}
      {compositeNote(controlId, error, helpText)}
    </div>
  );
}

/** The line a control is described by: its error, else its help text, else nothing. */
function describedByOf(
  controlId: string,
  shownError: string | null | undefined,
  helpText: string | null | undefined,
): string | undefined {
  if (shownError) return `${controlId}-error`;
  return helpText ? `${controlId}-hint` : undefined;
}

/** What the file field says it holds: the chosen file's name, or that a file is stored. */
function selectedFileName(value: unknown): string | null {
  if (value instanceof File) return value.name;
  return value ? "File selected" : null;
}

export function FieldRenderer({
  field,
  value,
  error,
  disabled,
  readOnly = false,
  required,
  onChange,
  onBrowse,
  testIdPrefix = "field",
}: FieldRendererProps) {
  const reactId = useId();
  const controlId = `${testIdPrefix}-${field.id}-${reactId}`;
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (next: unknown) => onChange(field.id, next),
    [field.id, onChange],
  );

  const shownError = fileError ?? error;
  const validationState = shownError ? ("error" as const) : ("none" as const);
  const hint = field.helpText || undefined;
  const testId = `${testIdPrefix}-${field.id}`;
  const describedBy = describedByOf(controlId, shownError, field.helpText);

  switch (field.controlType) {
    case "text":
    case "url":
    case "email":
    case "password":
    case "number": {
      const masked = readOnly && field.controlType === "password";
      const inputType = masked ? "text" : field.controlType;
      return (
        <SkyInput
          id={controlId}
          testId={testId}
          label={field.name}
          // SkyInput renders its own show/hide toggle for a password; adding a
          // second one here is how the form ends up with two eyes.
          type={inputType}
          // #2480: a source's user name and password are not the person's own login. The browser
          // is told not to fill them (new-password on the secret, off beside it) and password
          // managers are asked to skip them. Chrome and Edge treat this as a hint and may still
          // offer to save after Save, which no attribute can switch off for a password input.
          autoComplete={field.controlType === "password" ? "new-password" : "off"}
          data-1p-ignore
          data-lpignore="true"
          value={
            masked
              ? MASK_FOR_READER.repeat(String(value ?? "") === "" ? 0 : 8)
              : String(value ?? "")
          }
          placeholder={field.placeholder}
          disabled={disabled}
          required={required}
          hint={hint}
          validationState={validationState}
          validationMessage={shownError ?? undefined}
          onChange={(event) => handleChange(event.target.value)}
        />
      );
    }

    case "textarea":
      return (
        <SkyTextarea
          id={controlId}
          testId={testId}
          label={field.name}
          value={String(value ?? "")}
          placeholder={field.placeholder}
          disabled={disabled}
          required={required}
          resize="vertical"
          hint={hint}
          validationState={validationState}
          validationMessage={shownError ?? undefined}
          onChange={(event) => handleChange(event.target.value)}
        />
      );

    case "select":
      return (
        <SkyDropdown
          testId={testId}
          label={field.name}
          required={required}
          options={toDropdownOptions(field.options)}
          selectedOptions={value ? [String(value)] : []}
          disabled={disabled}
          placeholder={field.placeholder || "Select an option"}
          validationState={validationState}
          validationMessage={shownError ?? hint}
          onOptionSelect={(selected) => handleChange(selected[0] ?? "")}
        />
      );

    case "multiselect":
      return (
        <SkyDropdown
          testId={testId}
          label={field.name}
          required={required}
          multiselect
          options={toDropdownOptions(field.options)}
          selectedOptions={Array.isArray(value) ? value.map(String) : []}
          disabled={disabled}
          placeholder={field.placeholder || "Select options"}
          validationState={validationState}
          validationMessage={shownError ?? hint}
          onOptionSelect={(selected) => handleChange(selected)}
        />
      );

    case "checkbox":
      return (
        <SingleCheckbox
          testId={testId}
          label={field.name}
          description={hint}
          checked={value === true || value === "true"}
          disabled={disabled}
          required={required}
          validationState={validationState}
          validationMessage={shownError ?? undefined}
          onChange={handleChange}
        />
      );

    case "radio":
      return (
        <SkyRadioGroup
          testId={testId}
          label={field.name}
          required={required}
          layout="vertical"
          options={toRadioOptions(field.options)}
          value={String(value ?? "")}
          disabled={disabled}
          validationState={validationState}
          validationMessage={shownError ?? hint}
          onChange={handleChange}
        />
      );

    case "toggle":
      return (
        <div className="flex flex-col gap-1.5">
          <SkySwitch
            testId={testId}
            id={controlId}
            label={field.name}
            description={hint}
            checked={value === true || value === "true"}
            disabled={disabled}
            aria-describedby={describedBy}
            onCheckedChange={handleChange}
          />
          {shownError ? (
            <SkyText as="span" size={200} color="danger" id={`${controlId}-error`} role="alert">
              {shownError}
            </SkyText>
          ) : null}
        </div>
      );

    case "date":
      return (
        <SkyDatePicker
          id={controlId}
          testId={testId}
          label={field.name}
          required={required}
          value={parseDateValue(value)}
          disabled={disabled}
          hint={hint}
          validationState={validationState}
          validationMessage={shownError ?? undefined}
          onSelectDate={(date) => handleChange(formatDateValue(date))}
        />
      );

    case "datetime":
      return (
        <SkyDateTimePicker
          id={controlId}
          testId={testId}
          label={field.name}
          required={required}
          value={parseDateTimeValue(value)}
          disabled={disabled}
          hint={hint}
          validationState={validationState}
          validationMessage={shownError ?? undefined}
          onSelectDateTime={(date) => handleChange(formatDateTimeValue(date))}
        />
      );

    case "file": {
      // The known skypoint-ui gap (GUIDELINES.md §3.1): a native input styled by
      // the library's own button, never a second design system's uploader.
      const accept = field.fileConfig?.accept?.join(",");
      const selectedName = selectedFileName(value);
      return (
        <CompositeField
          labelId={`${controlId}-label`}
          controlId={controlId}
          label={field.name}
          required={required}
          helpText={field.helpText}
          error={shownError}
        >
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              id={controlId}
              type="file"
              className="sr-only"
              accept={accept}
              disabled={disabled}
              required={required}
              aria-invalid={shownError ? true : undefined}
              aria-describedby={describedBy}
              data-testid={`${testId}-input`}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                const maxMb = field.fileConfig?.maxSizeMB;
                if (maxMb && file.size > maxMb * 1024 * 1024) {
                  setFileError(`That file is larger than ${maxMb}MB. Choose a smaller file.`);
                  onChange(field.id, undefined);
                  return;
                }
                setFileError(null);
                if (field.fileConfig?.encodeTo === "base64") {
                  const reader = new FileReader();
                  reader.onload = () => handleChange(reader.result);
                  reader.onerror = () => {
                    logger.warn("connector form: could not read the selected file", {
                      "skydata.error_name": reader.error?.name ?? "unknown",
                    });
                    setFileError("We could not read that file. Choose it again.");
                    onChange(field.id, undefined);
                  };
                  reader.readAsDataURL(file);
                } else {
                  handleChange(file);
                }
              }}
            />
            <SkyButton
              appearance="secondary"
              disabled={disabled}
              testId={`${testId}-choose`}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file
            </SkyButton>
            {selectedName ? (
              <SkyText size={200} color="muted" truncate>
                {selectedName}
              </SkyText>
            ) : null}
          </div>
        </CompositeField>
      );
    }

    case "folderselector":
      return (
        <CompositeField
          labelId={`${controlId}-label`}
          controlId={controlId}
          label={field.name}
          required={required}
          helpText={field.helpText}
          error={shownError}
        >
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <SkyInput
                id={controlId}
                testId={testId}
                value={String(value ?? "")}
                placeholder={field.placeholder}
                readOnly
                disabled={disabled}
                validationState={validationState}
              />
            </div>
            <SkyButton
              appearance="secondary"
              icon={BrowseIcon}
              disabled={disabled || !onBrowse}
              testId={`${testId}-browse`}
              onClick={() => onBrowse?.(field.id)}
            >
              Browse
            </SkyButton>
          </div>
        </CompositeField>
      );

    case "keyvalue": {
      const pairs = toPairs(value);
      const update = (next: KeyValuePair[]) => handleChange(next);
      return (
        <CompositeField
          labelId={`${controlId}-label`}
          controlId={`${controlId}-0-key`}
          label={field.name}
          required={required}
          helpText={field.helpText}
          error={shownError}
        >
          <fieldset className="flex min-w-0 flex-col gap-2" aria-labelledby={`${controlId}-label`}>
            {pairs.map((pair, index) => (
              <div key={pair.id} className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <SkyInput
                    id={`${controlId}-${index}-key`}
                    testId={`${testId}-${index}-key`}
                    placeholder="Key"
                    aria-label={`${field.name} key ${index + 1}`}
                    value={pair.key}
                    disabled={disabled}
                    onChange={(event) => {
                      const next = [...pairs];
                      next[index] = { ...pair, key: event.target.value };
                      update(next);
                    }}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <SkyInput
                    id={`${controlId}-${index}-value`}
                    testId={`${testId}-${index}-value`}
                    placeholder="Value"
                    aria-label={`${field.name} value ${index + 1}`}
                    value={pair.value}
                    disabled={disabled}
                    onChange={(event) => {
                      const next = [...pairs];
                      next[index] = { ...pair, value: event.target.value };
                      update(next);
                    }}
                  />
                </div>
                <SkyButton
                  appearance="subtle"
                  icon={DeleteIcon}
                  disabled={disabled || pairs.length <= 1}
                  aria-label={`Remove ${field.name} pair ${index + 1}`}
                  testId={`${testId}-${index}-remove`}
                  onClick={() => {
                    const next = pairs.filter((_, position) => position !== index);
                    update(next.length > 0 ? next : [{ id: nextPairId(), key: "", value: "" }]);
                  }}
                />
              </div>
            ))}
            <div>
              <SkyButton
                appearance="subtle"
                icon={AddIcon}
                disabled={disabled}
                testId={`${testId}-add`}
                onClick={() => update([...pairs, { id: nextPairId(), key: "", value: "" }])}
              >
                Add pair
              </SkyButton>
            </div>
          </fieldset>
        </CompositeField>
      );
    }

    default: {
      // A seed written against a newer schema. Saying so beats rendering nothing:
      // a missing field reads as a broken screen, a named one as a stale image.
      const unknownType: string = field.controlType;
      logger.warn("connector form: unsupported control type", {
        "skydata.control_type": unknownType,
      });
      return (
        <SkyInput
          id={controlId}
          testId={testId}
          label={field.name}
          disabled
          value=""
          placeholder="This field type is not supported yet"
          validationState="warning"
          validationMessage={`skyData cannot render a "${unknownType}" field yet. Ask an administrator to update the connector.`}
        />
      );
    }
  }
}
